import {
    AuthContext,
    BaseDialog,
    ProviderCollectionInvoice,
    ToastContext,
    Variables,
    generateChunkedPdf,
    renderAmount,
    renderDate,
} from '@enter/core';
import { mdiDownload, mdiFileDocumentOutline, mdiOpenInNew, mdiPrinterOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DialogContext } from 'services';
import { IObservedRootStore, PatientCollectionsTab } from 'stores';
import { buildInvoiceStatementPrintHtml } from './invoice-statement-print-html';
import * as S from './invoice-statement.styles';

const STATEMENT_RETURN_TABS: PatientCollectionsTab[] = ['needs', 'ready', 'outstanding', 'paid'];

export const InvoiceStatement: React.FC<IObservedRootStore> = inject('rootStore')(
    observer((props: IObservedRootStore) => {
        const { rootStore } = props;
        if (!rootStore) return <></>;

        const { collectionInvoiceId, accountId } = useParams<{
            collectionInvoiceId: string;
            accountId?: string;
        }>();
        const navigate = useNavigate();
        const location = useLocation();
        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        const toastContext = useContext(ToastContext);
        const store = rootStore.collectionInvoiceStore;

        const [invoice, setInvoice] = useState<ProviderCollectionInvoice | undefined>();
        const [loading, setLoading] = useState(true);
        const [downloadingPdf, setDownloadingPdf] = useState(false);

        const basePath = accountId ? `/${accountId}/patient-collections` : '/patient-collections';

        const handleClose = () => {
            const fromTab = (location.state as { fromTab?: string } | null)?.fromTab;
            const tab =
                fromTab && STATEMENT_RETURN_TABS.includes(fromTab as PatientCollectionsTab)
                    ? fromTab
                    : 'outstanding';
            navigate(`${basePath}/${tab}`);
        };

        useEffect(() => {
            document.title = 'Provider Dashboard - Patient Statement';
            let cancelled = false;
            (async () => {
                if (!collectionInvoiceId) return;
                try {
                    setLoading(true);
                    authContext.cancelGroup?.('patient-collections-invoice');
                    authContext.setGroup?.('patient-collections-invoice');
                    await store.loadIntegrations();
                    const inv = await store.loadInvoiceById(collectionInvoiceId);
                    if (!cancelled) setInvoice(inv);
                    if (!cancelled && !inv) {
                        toastContext.notify?.('Invoice not found', 'right');
                    }
                } catch (err) {
                    if (!cancelled) dialogContext.showError(err);
                } finally {
                    if (!cancelled) setLoading(false);
                }
            })();
            return () => {
                cancelled = true;
                authContext.cancelGroup?.('patient-collections-invoice');
            };
        }, [collectionInvoiceId]);

        const info = invoice?.invoicePatient?.info;
        const patientLabel = [info?.patientFirstName, info?.patientLastName].filter(Boolean).join(' ') || '—';
        const externalPatientId = invoice?.invoicePatient?.externalPatientId;
        const facilityLabel = invoice?.facilityName || 'the billing provider';
        const status = !invoice
            ? undefined
            : invoice.declineDate
              ? 'declined'
              : !invoice.postDate
                ? 'ready'
                : Number(invoice.currentBalance?.amount || 0) === 0
                  ? 'paid'
                  : 'sent';

        const statusLabel =
            status === 'ready'
                ? 'Ready to send'
                : status === 'sent'
                  ? 'Sent'
                  : status === 'paid'
                    ? 'Paid'
                    : status === 'declined'
                      ? 'Declined'
                      : undefined;

        const patientPageHref = externalPatientId
            ? accountId
                ? `/${accountId}/patients/details/${encodeURIComponent(externalPatientId)}`
                : `/patients/details/${encodeURIComponent(externalPatientId)}`
            : undefined;

        const printHtml = useMemo(() => {
            if (!invoice) return '';
            return buildInvoiceStatementPrintHtml(invoice, { patientPageHref });
        }, [invoice, patientPageHref]);

        const handlePrint = () => {
            if (!printHtml) return;
            const frame = document.createElement('iframe');
            frame.setAttribute('aria-hidden', 'true');
            frame.style.position = 'fixed';
            frame.style.right = '0';
            frame.style.bottom = '0';
            frame.style.width = '0';
            frame.style.height = '0';
            frame.style.border = '0';
            document.body.appendChild(frame);
            const doc = frame.contentDocument;
            if (!doc) {
                frame.remove();
                return;
            }
            doc.open();
            doc.write(printHtml);
            doc.close();
            const cleanup = () => frame.remove();
            frame.onload = () => {
                try {
                    frame.contentWindow?.focus();
                    frame.contentWindow?.print();
                } finally {
                    // Give the print dialog a moment before tearing down the frame.
                    window.setTimeout(cleanup, 1000);
                }
            };
        };

        const handleDownloadPdf = async () => {
            if (!invoice || !printHtml) return;
            try {
                setDownloadingPdf(true);
                const stamp = invoice.controlNumber || invoice.collectionInvoiceId || 'statement';
                await generateChunkedPdf({
                    htmlChunks: [printHtml],
                    filename: `Patient-Statement-${stamp}.pdf`,
                });
            } catch (err) {
                console.error(err);
                toastContext.notify?.('Unable to download PDF. Please try again.', 'left', 'warning');
            } finally {
                setDownloadingPdf(false);
            }
        };

        return (
            <BaseDialog
                open={true}
                fullScreen={true}
                expandable={true}
                onClose={handleClose}
                aria-label="Patient Statement"
                slotProps={{
                    paper: {
                        style: {
                            background: Variables.Colors.n15,
                        },
                    },
                }}
            >
                <S.OverlayBody>
                    <S.Toolbar>
                        <Icon path={mdiFileDocumentOutline} size={0.85} color={Variables.Swatches.White} />
                        <S.ToolbarTitle>Patient Statement</S.ToolbarTitle>
                        {statusLabel && <S.StatusChip $status={status}>{statusLabel}</S.StatusChip>}
                        <S.ToolbarActions>
                            <S.ToolbarGhostButton type="button" variant="unstyled" onClick={handleClose}>
                                Close
                            </S.ToolbarGhostButton>
                            <S.ToolbarOutlinedButton
                                type="button"
                                variant="unstyled"
                                onClick={handlePrint}
                                disabled={!invoice}
                            >
                                <Icon path={mdiPrinterOutline} size={0.7} color={Variables.Swatches.White} />
                                Print
                            </S.ToolbarOutlinedButton>
                            <S.ToolbarPrimaryButton
                                type="button"
                                variant="unstyled"
                                onClick={handleDownloadPdf}
                                disabled={!invoice || downloadingPdf}
                            >
                                <Icon path={mdiDownload} size={0.75} color={Variables.Swatches.White} />
                                {downloadingPdf ? 'Generating PDF…' : 'Download PDF'}
                            </S.ToolbarPrimaryButton>
                        </S.ToolbarActions>
                    </S.Toolbar>

                    <S.PaperWrap>
                        <S.Sheet id="invoice-statement-print-root">
                            {loading && <S.SheetPad>Loading statement…</S.SheetPad>}
                            {!loading && !invoice && <S.SheetPad>Invoice not found.</S.SheetPad>}
                            {!loading && invoice && (
                                <>
                                    <S.SheetHeader>
                                        <div>
                                            <S.Brand>ENTER</S.Brand>
                                            <S.ProviderName>
                                                {invoice.facilityName || 'Billing provider'}
                                            </S.ProviderName>
                                            <S.Muted>
                                                {[
                                                    invoice.renderingProviderFirstName,
                                                    invoice.renderingProviderLastName,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                            </S.Muted>
                                        </div>
                                        <S.SheetHeaderRight>
                                            <S.SheetTitle>
                                                Statement of
                                                <br />
                                                Patient Responsibility
                                            </S.SheetTitle>
                                            <S.MetaGrid>
                                                <span>Statement #</span>
                                                <strong>{invoice.controlNumber || '—'}</strong>
                                                <span>Statement date</span>
                                                <strong>
                                                    {renderDate(invoice.postDate || invoice.approveDate) || '—'}
                                                </strong>
                                                <span>Account</span>
                                                <strong>{externalPatientId || '—'}</strong>
                                            </S.MetaGrid>
                                        </S.SheetHeaderRight>
                                    </S.SheetHeader>

                                    <S.TwoCol>
                                        <div>
                                            <S.SectionEyebrow>Billed to</S.SectionEyebrow>
                                            <S.PatientName>{patientLabel}</S.PatientName>
                                            <S.Muted>
                                                {[
                                                    info?.patientAddress1,
                                                    info?.patientCity,
                                                    info?.patientState,
                                                    info?.patientZip,
                                                ]
                                                    .filter(Boolean)
                                                    .join(', ') || '—'}
                                                <br />
                                                Patient ID {externalPatientId || '—'}
                                                {info?.patientDateOfBirth
                                                    ? ` · DOB ${renderDate(info.patientDateOfBirth)}`
                                                    : ''}
                                            </S.Muted>
                                            {patientPageHref && (
                                                <S.EnterPatientLink
                                                    href={patientPageHref}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <Icon path={mdiOpenInNew} size={0.55} />
                                                    Open Enter Patient Page
                                                </S.EnterPatientLink>
                                            )}
                                        </div>
                                        <div>
                                            <S.SectionEyebrow>Visit summary</S.SectionEyebrow>
                                            <S.VisitMetaGrid>
                                                <span>Date of service</span>
                                                <strong>{renderDate(invoice.serviceDate) || '—'}</strong>
                                                <span>Rendering provider</span>
                                                <strong>
                                                    {[
                                                        invoice.renderingProviderFirstName,
                                                        invoice.renderingProviderLastName,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' ') || '—'}
                                                </strong>
                                                <span>Insurance</span>
                                                <strong>{invoice.primaryPayerName || '—'}</strong>
                                            </S.VisitMetaGrid>
                                        </div>
                                    </S.TwoCol>

                                    <S.LinesTable>
                                        <thead>
                                            <tr>
                                                <th>Description</th>
                                                <th>Code</th>
                                                <th data-right="true">Charge</th>
                                                <th data-right="true">Insurance</th>
                                                <th data-right="true">Your resp.</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(invoice.invoiceItems || []).length === 0 && (
                                                <tr>
                                                    <td colSpan={5}>
                                                        <S.Muted>No line items on this invoice.</S.Muted>
                                                    </td>
                                                </tr>
                                            )}
                                            {(invoice.invoiceItems || []).map((li, i) => (
                                                <tr key={li.collectionInvoiceItemId || i}>
                                                    <td>{li.procedureCptDescription || '—'}</td>
                                                    <td>{li.procedureCpt || '—'}</td>
                                                    <td data-right="true">
                                                        {renderAmount(li.procedureCharge)}
                                                    </td>
                                                    <td data-right="true">{renderAmount(li.payerPayments)}</td>
                                                    <td data-right="true">
                                                        <strong>
                                                            {renderAmount(li.patientResponsibility)}
                                                        </strong>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </S.LinesTable>

                                    <S.Totals>
                                        <S.TotalRow>
                                            <span>Total charges</span>
                                            <span>{renderAmount(invoice.invoiceAmount)}</span>
                                        </S.TotalRow>
                                        <S.TotalRow>
                                            <span>Patient payments</span>
                                            <span>−{renderAmount(invoice.patientPayments)}</span>
                                        </S.TotalRow>
                                        <S.TotalRow>
                                            <span>Discounts</span>
                                            <span>−{renderAmount(invoice.patientDiscounts)}</span>
                                        </S.TotalRow>
                                        <S.DueBox>
                                            <span>Amount due</span>
                                            <strong>{renderAmount(invoice.currentBalance)}</strong>
                                        </S.DueBox>
                                    </S.Totals>

                                    <S.StatementFooter className="ps-footer">
                                        <div>
                                            <S.FooterEyebrow>How to pay</S.FooterEyebrow>
                                            <S.FooterBody>
                                                Contact{' '}
                                                <S.FooterAccent>{facilityLabel}</S.FooterAccent>
                                                {externalPatientId ? (
                                                    <>
                                                        {' '}
                                                        and reference account{' '}
                                                        <S.FooterAccent>{externalPatientId}</S.FooterAccent>
                                                    </>
                                                ) : null}
                                                . Checks payable to {facilityLabel}.
                                            </S.FooterBody>
                                        </div>
                                        <div>
                                            <S.FooterEyebrow>Questions about this statement?</S.FooterEyebrow>
                                            <S.FooterBody>
                                                {invoice.memoNotes?.trim() ||
                                                    `Contact ${facilityLabel} with questions about this statement.`}
                                            </S.FooterBody>
                                        </div>
                                    </S.StatementFooter>
                                </>
                            )}
                        </S.Sheet>
                    </S.PaperWrap>
                </S.OverlayBody>
            </BaseDialog>
        );
    }),
);
